import React, { useEffect, useState } from "react";
import { TopFrame } from "@/components/dashboard/TopFrame";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AccountTab from "@/components/sales/AccountTab";
import CustomerPrioritiesTab from "@/components/sales/CustomerPrioritiesTab";
import CustomerKPIsTab from "@/components/sales/CustomerKPIsTab";
import CustomerOrgChartTab from "@/components/sales/CustomerOrgChartTab";
import BusinessValueFrameworkTab from "@/components/sales/BusinessValueFrameworkTab";
import AISalesAdvisorTab from "@/components/sales/AISalesAdvisorTab";
import KeyDealsTab from "@/components/sales/KeyDealsTab";
import OrchestrationTab from "@/components/sales/OrchestrationTab";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { useSalesTabs } from "@/hooks/useSalesTabs";

type Account = {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
};

const Sales = () => {
  const [user, setUser] = useState<User | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [selectedAccount_row, setSelectedAccount_row] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("account");
  const [showAlert, setShowAlert] = useState(false);
  const { config: salesTabsConfig, isLoading: salesTabsLoading } = useSalesTabs();

  useEffect(() => {
    const getUser = async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleTabChange = (value: string) => {
    if (value !== "account" && !selectedAccount) {
      setShowAlert(true);
      return;
    }
    setActiveTab(value);
  };

  return <div className="min-h-screen bg-secondary">
      <TopFrame user={user} hideProgress={true} />
      <AlertDialog open={showAlert} onOpenChange={setShowAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No Account Selected</AlertDialogTitle>
            <AlertDialogDescription>
              Please select an account in the Accounts tab before proceeding to other sections.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowAlert(false)}>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="w-full justify-center bg-secondary/50 border border-primary/20">
            <TabsTrigger value="account" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
              Accounts
            </TabsTrigger>
            <TabsTrigger value="orgchart" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
              Customer Organization
            </TabsTrigger>
            {salesTabsConfig?.customer_priorities && (
              <TabsTrigger value="priorities" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Customer Priorities
              </TabsTrigger>
            )}
            {salesTabsConfig?.customer_kpis && (
              <TabsTrigger value="kpis" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Customer KPIs
              </TabsTrigger>
            )}
            {salesTabsConfig?.business_value_framework && (
              <TabsTrigger value="framework" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Business Value Framework
              </TabsTrigger>
            )}
            {salesTabsConfig?.ai_sales_advisor && (
              <TabsTrigger value="advisor" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                AI Sales Advisor
              </TabsTrigger>
            )}
            <TabsTrigger value="deals" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
              Key Deals
            </TabsTrigger>
            <TabsTrigger value="orchestration" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
              Account Orchestration
            </TabsTrigger>
          </TabsList>
          <TabsContent value="account">
            <AccountTab onAccountSelect={setSelectedAccount} selectedAccount_row={selectedAccount_row} setSelectedAccount_row={setSelectedAccount_row} />
          </TabsContent>
          <TabsContent value="orgchart">
            <CustomerOrgChartTab selectedAccount={selectedAccount} />
          </TabsContent>
          {salesTabsConfig?.customer_priorities && (
            <TabsContent value="priorities">
              <CustomerPrioritiesTab selectedAccount={selectedAccount} />
            </TabsContent>
          )}
          {salesTabsConfig?.customer_kpis && (
            <TabsContent value="kpis">
              <CustomerKPIsTab selectedAccount={selectedAccount} />
            </TabsContent>
          )}
          {salesTabsConfig?.business_value_framework && (
            <TabsContent value="framework">
              <BusinessValueFrameworkTab selectedAccount={selectedAccount} />
            </TabsContent>
          )}
          {salesTabsConfig?.ai_sales_advisor && (
            <TabsContent value="advisor">
              <AISalesAdvisorTab selectedAccount={selectedAccount} />
            </TabsContent>
          )}
          <TabsContent value="deals">
            <KeyDealsTab selectedAccount={selectedAccount} />
          </TabsContent>
          <TabsContent value="orchestration">
            <OrchestrationTab selectedAccount={selectedAccount} />
          </TabsContent>
        </Tabs>
      </div>
    </div>;
};

export default Sales;
